import { getLocal, LocalStorageKeys } from './localStorage';
import { serviceHelper } from '../config/service-helper';
import {
  enrichBasketApiResponseWithAlgoliaData,
  shouldEnrichBasketApiResponse,
} from './cart/enrichBasketApiResponseWithAlgoliaData';
import Cookies from 'js-cookie';
import { COOKIES } from '../config/cookies';
import { spendAndSavePrefix } from '@jbhi-fi/utils/src/toBasketSerialString';
import { toBasketPromos } from '@jbhi-fi/utils/src/toBasketPromos';
import { toAllDiscounts } from '@jbhi-fi/utils/src/toAllDiscounts';
import { IsAttachedService } from '../templates/components/pdp/utils/product-service';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { ShopifyStorefrontClient } from '../api/shopify/ShopifyStorefrontClient';
import { transformStorefrontCartToJbCart } from './cart/transformStorefrontCartToJbCart';
import { ProductClient } from '../api/algolia/ProductClient';
import { Cart, CartResponseLineItemType, CartResponseType } from '@jbhi-fi/apis/src/cart/types';
import { ExistingLineItem } from '@jbhi-fi/apis/src/storefront/StorefrontClient';
import { getCoupons } from './coupons';
import { JbCart, JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';
import { Coupon } from '@jbhi-fi/apis/src/customer';
import { CartCoupon } from '../contexts/CouponContextProvider/types';

const storefrontClient = new ShopifyStorefrontClient();
const productClient = new ProductClient();

const doAttributesNeedUpdate = (
  existingAttributes: Record<string, string | undefined>,
  newAttributes: Record<string, string | undefined>
) => {
  for (const key in newAttributes) {
    if (existingAttributes[key] !== newAttributes[key]) {
      return true;
    }
  }
  return false;
};

const cartCoupons = (cart: JbCart) => {
  const coupons = cart.attributes?._coupons ? JSON.parse(cart.attributes._coupons) : [];
  return coupons.map((item: { code?: string }) => {
    return item?.code;
  });
};

const getCouponCodes = (coupons: Array<Coupon>): Array<string> =>
  Array.isArray(coupons) ? coupons.map(coupon => coupon.couponCode) : [];

export const getPrimaryItem = (items: Array<JbCartItem>, serviceLink: string | undefined) => {
  return items.find(
    item => item.properties?._serviceLink && item.properties?._serviceLink === serviceLink && !IsAttachedService(item)
  );
};

const shopifyCartToCartApiData = (cart: JbCart) => {
  const cartData: Cart = {
    CartType: window.Jbhifi.customerDetails?.isPerks ? 'Perks' : 'Retail',
    LineItems: [],
    LocationId: 115,
    CouponReferences: cartCoupons(cart),
    CouponReferencesWallet: getCouponCodes(window.Jbhifi.coupons),
    // @ts-ignore
    isNewCart: true, // for BFF backwards compatibility with old cart. Can be removed once new cart is live
  };

  cart.items.forEach(item => {
    const serviceLinks = [];
    let quantity = item.quantity;
    if (IsAttachedService(item)) {
      const primaryItem = getPrimaryItem(cart.items, item.properties?._serviceLink);

      if (primaryItem) quantity = primaryItem.quantity;
    }
    if (item.properties._serviceLink) serviceLinks.push(item.properties._serviceLink);

    cartData.LineItems.push({
      Id: item.line_id,
      SKU: item.sku,
      Quantity: quantity,
      UnitPriceInc: item.final_line_price / 100,
      ServiceLinks: serviceLinks,
      TieredId: item.properties?._marketplaceTieredId,
      IsAttachedService: IsAttachedService(item),
    });
  });

  return cartData;
};

export const getBonusItems = async (cartResponse: CartResponseType) => {
  const removedBonusSkus = getLocal(LocalStorageKeys.RemovedBonusSkus, true) || [];
  const allBonusSkus = cartResponse.BonusProducts?.map(bonusProduct => bonusProduct.Details.Sku) || [];
  const bonusSkus = allBonusSkus.filter(sku => !removedBonusSkus.includes(sku));
  if (!bonusSkus) return;
  const bonusProducts = (await productClient.getProductsFromSkus(bonusSkus))?.data?.products;
  window.Jbhifi.bonusProducts = bonusProducts;
  if (!bonusProducts?.length) return;
  const properties = {
    _isBonusItem: true,
  };
  const updatedCart = await storefrontClient.addMultipleToCart(
    bonusProducts.map(product => ({ variantId: product.variant_id, attributes: filterAndMapAttributes(properties) }))
  );
  return updatedCart ? transformStorefrontCartToJbCart(updatedCart) : undefined;
};

export const splitMismatchedItems = async (cartResponse: CartResponseType, cart: JbCart) => {
  const unsplitCartItems = cartResponse.LineItems.filter(item => item.Id.match(/-\d$/));
  if (!unsplitCartItems.length) return;
  let updatedCart;
  for (const unsplitItem of unsplitCartItems) {
    const originalItemId = unsplitItem.Id.replace(/-\d$/, '');
    const originalItem = cart.items.find(item => item.line_id === originalItemId);
    if (!originalItem) continue;
    await storefrontClient.updateCartLine(originalItem.line_id, 1);
    updatedCart = await storefrontClient.addToCart(originalItem.id, 1);
  }
  return updatedCart ? transformStorefrontCartToJbCart(updatedCart) : undefined;
};

export const updateCartWithCartApiResponse = async (cartResponse: CartResponseType, cart: JbCart) => {
  const userAgent = window?.navigator?.userAgent;

  const cartApiPromos = toBasketPromos(cartResponse.LineItems);
  const allDiscounts = toAllDiscounts(cartResponse.LineItems);

  const couponsInWallet = cartResponse?.WalletCouponsEvaluated || [];
  const primaryItemsToUpdate = cart.items?.map(item => {
    const hasServiceOffering = cartResponse.LineItems.some(
      cartResponseItem => cartResponseItem.Id === item.line_id && cartResponseItem.Services.length
    );
    const itemPromo = cartApiPromos.find(promo => promo?.promoId === item.line_id);
    const itemDiscounts = allDiscounts.filter(discount => discount.lineId === item.line_id);

    // Merge items that have no promos or services
    if (!itemPromo && !hasServiceOffering && !item.properties._serviceLink) {
      delete item.properties._shouldSplit;
    }

    return {
      lineId: item.line_id,
      quantity: item.quantity,
      attributes: filterAndMapAttributes({
        ...item.properties,
        _promotion_id: itemPromo?.promoId,
        _allDiscounts: JSON.stringify(itemDiscounts),
      }),
    };
  });

  const servicesToUpdate: Array<ExistingLineItem> = [];
  cart.items
    .filter(item => item.properties._isAttachedService === 'true')
    .forEach(item => {
      const basketItem = cartResponse.LineItems.find(basketItem => basketItem.Id === item.line_id);
      if (!basketItem) return;

      const hasPriceMismatch = basketItem.LineTotalInc * 100 !== item.final_line_price;
      const hasValidationErrors = basketItem.ValidationErrors?.length > 0;
      if (hasPriceMismatch || hasValidationErrors) {
        servicesToUpdate.push({
          lineId: item.line_id,
          attributes: filterAndMapAttributes({
            ...item.properties,
            _servicePrice: basketItem.LineTotalInc.toString(),
            _hasServiceErrors: hasValidationErrors,
          }),
        });
      }
    });

  let updatedCart;
  const cartLinesToUpdate = [...primaryItemsToUpdate, ...servicesToUpdate];
  if (cartLinesToUpdate?.length) {
    updatedCart = await storefrontClient.updateCartLines(cartLinesToUpdate);
  }

  let lineItemsCouponPromotions: CartResponseLineItemType['Promotions'] = [];
  cartResponse.LineItems.forEach(lineItem => {
    const itemCouponDiscount = lineItem.Promotions.filter(
      promo => promo && promo.Reference && promo.PromotionType === 'Coupon'
    );
    lineItemsCouponPromotions = [...lineItemsCouponPromotions, ...itemCouponDiscount];
  });

  const transformedCartDiscounts = JSON.stringify(
    lineItemsCouponPromotions
      .filter(promotion => promotion.CouponName.startsWith(spendAndSavePrefix))
      .map(promotion => {
        return {
          code: promotion.Reference,
          message: promotion.Name,
          amount: promotion.DiscountInc,
        };
      })
  );

  const cartLevelDiscountCoupon = lineItemsCouponPromotions.find(promotion =>
    promotion.CouponName.startsWith(spendAndSavePrefix)
  )?.Reference;
  let cartLevelExcludedSkus;
  if (cartLevelDiscountCoupon) {
    cartLevelExcludedSkus = JSON.stringify(
      cartResponse.LineItems.filter(
        item =>
          !item.Promotions.length ||
          (item.Promotions.length && !item.Promotions.find(promo => promo.Reference === cartLevelDiscountCoupon))
      ).map(item => item.SKU)
    );
  }

  const riskifiedToken = Cookies.get(COOKIES.RISKIFIED_TOKEN);
  const { coupons: updatedCoupons } = await getCoupons(undefined, cart, cartResponse);

  const existingCoupons: Array<CartCoupon> = JSON.parse(cart.attributes._coupons || '[]');
  const existingCouponsMap = new Map(existingCoupons.map(coupon => [coupon.code, coupon]));
  const uniqueCoupons = updatedCoupons.map(coupon => ({
    ...existingCouponsMap.get(coupon.code),
    ...coupon,
  }));

  const newAttributes = {
    _cartDiscounts: transformedCartDiscounts,
    _userAgent: userAgent,
    _riskifiedToken: riskifiedToken,
    _basketSerial: JSON.stringify(cartApiPromos),
    _excludedSkus: cartLevelExcludedSkus,
    _coupons: JSON.stringify(uniqueCoupons),
    _couponsInWallet: JSON.stringify(couponsInWallet),
  };

  if (doAttributesNeedUpdate(cart.attributes, newAttributes)) {
    updatedCart = await storefrontClient.setCartAttributes(cart.attributes, newAttributes);
  }

  return transformStorefrontCartToJbCart(updatedCart);
};

export const getCartApiResponse = async (cart: JbCart): Promise<CartResponseType | undefined> => {
  if (!cart) {
    console.error('No cart provided to getBasketResponse');
    return;
  }
  const basketPostData = shopifyCartToCartApiData(cart);
  const calculatePricesUrl = `${serviceHelper.themeApisUrl}/cartApi`;

  const response = await fetch(calculatePricesUrl, {
    method: 'POST',
    body: JSON.stringify(basketPostData),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    console.error('Error fetching basket response', response);
    return;
  }
  let basketResponse = (await response.json())?.data;

  // No proper response
  if (!basketResponse || !basketResponse.LineItems || !basketResponse.LineItems.length) {
    return;
  }

  if (shouldEnrichBasketApiResponse(cart)) {
    try {
      basketResponse = await enrichBasketApiResponseWithAlgoliaData(basketResponse);
    } catch (e) {
      console.error('Error fetching algolia data', e);
    }
  }
  return basketResponse;
};
